import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Container, Card, CardContent, Button, Grid, TextField, Tabs, Tab, InputAdornment, IconButton, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import BuildIcon from '@mui/icons-material/Build';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import HistoryIcon from '@mui/icons-material/History';
import LockIcon from '@mui/icons-material/Lock';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLogbook } from '../../contexts/LogbookContext';
import axios from '../users/axios';
import { getAuthHeaders } from '../../utils/api';
import { validatePassword, getPasswordStrength } from '../../utils/passwordValidation';

// USP features for the landing page
const uspFeatures = [
    {
        icon: DirectionsBoatIcon,
        title: 'Skapa tripploggar',
        description: 'Dokumentera dina båtäventyr med detaljerade loggar. Lägg till avgångs- och ankomsttider, väder, vind och position.',
    },
    {
        icon: BuildIcon,
        title: 'Spara serviceloggar',
        description: 'Håll koll på allt underhåll. Dokumentera motorservice, rigg, elektronik och mer med bilder och kostnader.',
    },
    {
        icon: PhotoLibraryIcon,
        title: 'Organisera med bilder',
        description: 'Lägg till bilder till både tripp- och serviceloggar. Skapa en visuell dokumentation av dina äventyr.',
    },
    {
        icon: HistoryIcon,
        title: 'Spara för framtiden',
        description: 'Bygg upp en komplett historik över dina båtäventyr och servicearbeten som du kan gå tillbaka till.',
    },
    {
        icon: LockIcon,
        title: 'Privat och säkert',
        description: 'Allt innehåll är privat och endast synligt för dig. Du bestämmer själv vem som har tillgång.',
    },
    {
        icon: PersonAddIcon,
        title: 'Bjud in användare',
        description: 'Bjud in familj och vänner att dela dina loggar. De kan se och bidra till dokumentationen.',
    },
];

// Getting started steps
const gettingStartedSteps = [
    {
        number: 1,
        title: 'Skapa ditt konto',
        description: 'Klicka på "Skapa konto" ovan och ange namn, mejladress och önskat lösenord. Verifiera din e-postadress via länken som skickas till dig.',
    },
    {
        number: 2,
        title: 'Börja dokumentera',
        description: 'Logga in och börja skapa tripploggar och serviceloggar. Lägg till bilder och beskrivningar för att dokumentera dina båtäventyr.',
    },
    {
        number: 3,
        title: 'Bjud in användare',
        description: 'Du kan bjuda in familj och vänner att dela dina loggar. De behöver skapa egna konton och ange inbjudningskoden du ger dem.',
    },
];

function Info() {
    const { currentUser, isLoggedIn, login } = useAuth();
    const { logbooks, hasLogbooks, refreshLogbooks } = useLogbook();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [tabValue, setTabValue] = useState(0);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Signup state
    const [signupUsername, setSignupUsername] = useState('');
    const [signupEmail, setSignupEmail] = useState('');
    const [signupPassword, setSignupPassword] = useState('');
    const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
    const [showSignupPassword, setShowSignupPassword] = useState(false);
    const [showSignupConfirmPassword, setShowSignupConfirmPassword] = useState(false);
    const [signupError, setSignupError] = useState('');
    const [signupLoading, setSignupLoading] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordStrength, setPasswordStrength] = useState('');
    const [signupSuccess, setSignupSuccess] = useState(false);

    // Forgot password state
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
    const [forgotPasswordError, setForgotPasswordError] = useState('');
    const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
    const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

    const [createLogbookDialogOpen, setCreateLogbookDialogOpen] = useState(false);
    const [confirmSecondLogbookOpen, setConfirmSecondLogbookOpen] = useState(false);
    const [userDismissedCreateLogbookDialog, setUserDismissedCreateLogbookDialog] = useState(false);
    const [newLogbookName, setNewLogbookName] = useState('');
    const [creatingLogbook, setCreatingLogbook] = useState(false);
    const [createLogbookError, setCreateLogbookError] = useState('');

    // Email verification state
    const [emailNotVerified, setEmailNotVerified] = useState(false);
    const [unverifiedEmail, setUnverifiedEmail] = useState('');
    const [resendLoading, setResendLoading] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);
    const [resendError, setResendError] = useState('');

    // Nollställ "avvisad" när användaren får loggböcker (t.ex. skapat eller gått med)
    useEffect(() => {
        if (hasLogbooks) setUserDismissedCreateLogbookDialog(false);
    }, [hasLogbooks]);

    const ownedLogbooks = useMemo(
        () => (logbooks || []).filter((lb) => lb.isOwner),
        [logbooks]
    );
    const firstOwnedLogbookDisplayName = useMemo(() => {
        const first = ownedLogbooks[0];
        return first ? (first.name || 'Min loggbok') : '';
    }, [ownedLogbooks]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setError('');
        setSignupError('');
        setShowForgotPassword(false);
        setForgotPasswordSuccess(false);
        setSignupSuccess(false);
        setEmailNotVerified(false);
        setResendSuccess(false);
        setResendError('');
    };

    // Sync tab with URL search param (?tab=login | ?tab=signup)
    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab === 'signup') {
            setTabValue(1);
            window.scrollTo(0, 0);
        } else if (tab === 'login') {
            setTabValue(0);
            window.scrollTo(0, 0);
        }
    }, [searchParams]);

    // Handle resend verification email
    const handleResendVerification = async () => {
        setResendLoading(true);
        setResendError('');
        setResendSuccess(false);

        try {
            const response = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/resend-verification`, {
                email: unverifiedEmail,
            });

            setResendSuccess(true);
        } catch (error) {
            console.error('Resend verification error', error);
            if (error.response && error.response.status === 429) {
                setResendError(error.response.data.message || 'Vänta innan du begär en ny länk.');
            } else if (error.response && error.response.data && error.response.data.message) {
                setResendError(error.response.data.message);
            } else {
                setResendError('Ett fel uppstod. Försök igen.');
            }
        } finally {
            setResendLoading(false);
        }
    };

    // Handle signup password change with validation
    const handleSignupPasswordChange = (e) => {
        const newPassword = e.target.value;
        setSignupPassword(newPassword);
        
        if (newPassword.length === 0) {
            setPasswordError('');
            setPasswordStrength('');
            return;
        }
        
        const errors = validatePassword(newPassword);
        
        if (errors.length > 0) {
            setPasswordError(errors.join(', '));
            setPasswordStrength('weak');
        } else {
            setPasswordError('');
            setPasswordStrength(getPasswordStrength(newPassword));
        }
        
        // Check match if confirm password is already filled
        if (signupConfirmPassword && newPassword !== signupConfirmPassword) {
            setSignupError('Lösenorden matchar inte');
        } else if (signupConfirmPassword && newPassword === signupConfirmPassword) {
            setSignupError('');
        }
    };

    const handleSignupConfirmPasswordChange = (e) => {
        const newConfirmPassword = e.target.value;
        setSignupConfirmPassword(newConfirmPassword);
        
        if (newConfirmPassword && newConfirmPassword !== signupPassword) {
            setSignupError('Lösenorden matchar inte');
        } else {
            setSignupError('');
        }
    };

    // Handle signup form submission
    const handleSignup = async (event) => {
        event.preventDefault();
        setSignupError('');
        setSignupLoading(true);

        // Validate password
        const passwordErrors = validatePassword(signupPassword);
        if (passwordErrors.length > 0) {
            setSignupError(`Lösenordet uppfyller inte kraven: ${passwordErrors.join(', ')}`);
            setSignupLoading(false);
            return;
        }

        // Check password match
        if (signupPassword !== signupConfirmPassword) {
            setSignupError('Lösenorden matchar inte');
            setSignupLoading(false);
            return;
        }

        try {
            const response = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/public-signup`, {
                username: signupUsername,
                email: signupEmail,
                password: signupPassword,
            });

            if (response.status === 201) {
                setSignupSuccess(true);
                // Clear form
                setSignupUsername('');
                setSignupEmail('');
                setSignupPassword('');
                setSignupConfirmPassword('');
                setPasswordError('');
                setPasswordStrength('');
            }
        } catch (error) {
            console.error('Signup error', error);
            if (error.response && error.response.data && error.response.data.message) {
                setSignupError(error.response.data.message);
            } else {
                setSignupError('Ett fel uppstod vid registrering. Försök igen.');
            }
        } finally {
            setSignupLoading(false);
        }
    };

    // Handle forgot password form submission
    const handleForgotPassword = async (event) => {
        event.preventDefault();
        setForgotPasswordError('');
        setForgotPasswordLoading(true);

        try {
            await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/forgot-password`, {
                email: forgotPasswordEmail,
            });

            setForgotPasswordSuccess(true);
            setForgotPasswordEmail('');
        } catch (error) {
            console.error('Forgot password error', error);
            if (error.response && error.response.data && error.response.data.message) {
                setForgotPasswordError(error.response.data.message);
            } else {
                setForgotPasswordError('Ett fel uppstod. Försök igen.');
            }
        } finally {
            setForgotPasswordLoading(false);
        }
    };

    const handleLogin = async (event) => {
        event.preventDefault();
        setError('');
        setEmailNotVerified(false);
        setResendSuccess(false);
        setResendError('');
        setLoading(true);

        try {
            const loginResponse = await axios.post(`${process.env.REACT_APP_SERVER_URL}/api/login`, {
                username,
                password,
            });

            if (loginResponse.data.token) {
                login(loginResponse.data.token);
                const redirect = searchParams.get('redirect');
                navigate(redirect ? decodeURIComponent(redirect) : '/info');
            } else {
                setError('Inloggningen misslyckades. Försök igen.');
            }
        } catch (error) {
            console.error('Login error', error);
            if (error.response && error.response.status === 429) {
                setError(error.response.data.message || 'För många inloggningsförsök. Försök igen om 15 minuter.');
            } else if (error.response && error.response.status === 403 && error.response.data.emailNotVerified) {
                // E-post inte verifierad
                setEmailNotVerified(true);
                setUnverifiedEmail(error.response.data.email);
                setError(error.response.data.message);
            } else if (error.response && error.response.data && error.response.data.message) {
                setError(error.response.data.message);
            } else {
                setError('Ett fel uppstod vid inloggning. Försök igen.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCreateLogbook = async () => {
        setCreateLogbookError('');
        setCreatingLogbook(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/logbooks`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ name: newLogbookName.trim() || undefined })
            });
            if (res.ok) {
                await refreshLogbooks();
                setCreateLogbookDialogOpen(false);
                setNewLogbookName('');
            } else {
                const data = await res.json().catch(() => ({}));
                setCreateLogbookError(data.message || 'Kunde inte skapa loggbok.');
            }
        } catch (err) {
            setCreateLogbookError('Något gick fel.');
        } finally {
            setCreatingLogbook(false);
        }
    };

    const handleOpenCreateLogbookClick = () => {
        if (ownedLogbooks.length === 0) {
            setCreateLogbookDialogOpen(true);
        } else {
            setConfirmSecondLogbookOpen(true);
        }
    };

    const handleConfirmSecondLogbookContinue = () => {
        setConfirmSecondLogbookOpen(false);
        setCreateLogbookDialogOpen(true);
    };

    // Logged out view - similar to familjesajten
    if (!isLoggedIn) {
        return (
            <Box
                    sx={{
                        minHeight: '100vh',
                        backgroundColor: '#F5F9FF',
                        backgroundImage: 'url(/images/bakgrund_loggboken_1000x1500.jpg)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        position: 'relative',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(to bottom, rgba(245, 249, 255, 0.95) 0%, rgba(245, 249, 255, 0.98) 100%)',
                            zIndex: 0,
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundImage: 'url(/images/bakgrund_loggboken_1000x1500.jpg)',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            opacity: 0.20,
                            zIndex: 0,
                        },
                    }}
                >
                    {/* Hero Section - 2 columns */}
                    <Box sx={{ position: 'relative', zIndex: 2, py: { xs: 5, md: 8 }, overflow: 'hidden' }}>
                        <Container maxWidth="lg">
                            <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center">
                                {/* Left column - Title and intro */}
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ pr: { md: 4 } }}>
                                        <Typography
                                        variant="h1"
                                        sx={{
                                            fontSize: { xs: '2rem', md: '2.5rem' },
                                            fontWeight: 700,
                                            color: '#1A237E',
                                            marginBottom: '16px',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        Dokumentera dina båtäventyr
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            fontSize: { xs: '1rem', md: '1.125rem' },
                                            color: '#212121',
                                            lineHeight: 1.7,
                                            marginBottom: '24px',
                                        }}
                                    >
                                        Skapa tripp- och serviceloggar, spara bilder och bygg upp en komplett historik över dina båtäventyr på ett säkert och organiserat sätt.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        onClick={() => setTabValue(1)}
                                        sx={{
                                            backgroundColor: '#1565C0',
                                            color: '#fff',
                                            textTransform: 'none',
                                            padding: '12px 32px',
                                            fontSize: '1rem',
                                            fontWeight: 500,
                                            borderRadius: 2,
                                            '&:hover': {
                                                backgroundColor: '#0d47a1',
                                            },
                                        }}
                                    >
                                        Skapa konto
                                    </Button>
                                </Box>
                            </Grid>

                            {/* Right column - Login card */}
                            <Grid item xs={12} md={6}>
                                <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
                                    <Card
                                        sx={{
                                            borderRadius: 3,
                                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                                            padding: { xs: '20px', sm: '24px', md: '32px' },
                                            maxWidth: '450px',
                                            width: '100%',
                                            boxSizing: 'border-box',
                                        }}
                                    >
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            color: '#1565C0',
                                            fontWeight: 700,
                                            marginBottom: '8px',
                                            textAlign: 'center',
                                            fontSize: { xs: '1.5rem', md: '1.75rem' },
                                        }}
                                    >
                                        Kom igång!
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#212121',
                                            marginBottom: '24px',
                                            textAlign: 'center',
                                            fontSize: '0.9rem',
                                        }}
                                    >
                                        Logga in eller skapa ett konto för att börja
                                    </Typography>

                                    {!showForgotPassword && (
                                        <Tabs
                                            value={tabValue}
                                            onChange={handleTabChange}
                                            sx={{
                                                marginBottom: '20px',
                                                '& .MuiTab-root': {
                                                    textTransform: 'none',
                                                    fontWeight: 500,
                                                    fontSize: '0.95rem',
                                                },
                                            }}
                                        >
                                            <Tab label="Logga in" />
                                            <Tab label="Skapa konto" />
                                        </Tabs>
                                    )}

                                    {/* Login form */}
                                    {tabValue === 0 && !showForgotPassword && (
                                        <form onSubmit={handleLogin}>
                                            <Box sx={{ marginBottom: '16px' }}>
                                                <TextField
                                                    label="Användarnamn eller e-post"
                                                    type="text"
                                                    value={username}
                                                    onChange={(e) => {
                                                        setUsername(e.target.value);
                                                        setError('');
                                                    }}
                                                    fullWidth
                                                    required
                                                    size="small"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                            <Box sx={{ marginBottom: '16px' }}>
                                                <TextField
                                                    label="Lösenord"
                                                    type={showPassword ? 'text' : 'password'}
                                                    value={password}
                                                    onChange={(e) => {
                                                        setPassword(e.target.value);
                                                        setError('');
                                                    }}
                                                    fullWidth
                                                    required
                                                    size="small"
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    aria-label="toggle password visibility"
                                                                    onClick={() => setShowPassword(!showPassword)}
                                                                    edge="end"
                                                                    size="small"
                                                                >
                                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: 2,
                                                        },
                                                    }}
                                                />
                                            </Box>
                                            {error && !emailNotVerified && (
                                                <Alert severity="error" sx={{ marginBottom: '16px', fontSize: '0.85rem' }}>
                                                    {error}
                                                </Alert>
                                            )}
                                            {emailNotVerified && (
                                                <Box sx={{ marginBottom: '16px' }}>
                                                    <Alert severity="warning" sx={{ marginBottom: '12px', fontSize: '0.85rem' }}>
                                                        {error}
                                                    </Alert>
                                                    {resendSuccess ? (
                                                        <Alert severity="success" sx={{ fontSize: '0.85rem' }}>
                                                            En ny verifieringslänk har skickats till {unverifiedEmail}. Kontrollera din inkorg.
                                                        </Alert>
                                                    ) : (
                                                        <Box>
                                                            {resendError && (
                                                                <Alert severity="error" sx={{ marginBottom: '8px', fontSize: '0.85rem' }}>
                                                                    {resendError}
                                                                </Alert>
                                                            )}
                                                            <Button
                                                                variant="outlined"
                                                                fullWidth
                                                                onClick={handleResendVerification}
                                                                disabled={resendLoading}
                                                                sx={{
                                                                    textTransform: 'none',
                                                                    borderRadius: 2,
                                                                    borderColor: '#1565C0',
                                                                    color: '#1565C0',
                                                                    '&:hover': {
                                                                        borderColor: '#0d47a1',
                                                                        backgroundColor: 'rgba(21, 101, 192, 0.04)',
                                                                    },
                                                                }}
                                                            >
                                                                {resendLoading ? 'Skickar...' : 'Skicka ny verifieringslänk'}
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                fullWidth
                                                disabled={loading}
                                                sx={{
                                                    backgroundColor: '#1565C0',
                                                    color: '#fff',
                                                    textTransform: 'none',
                                                    padding: '10px',
                                                    fontSize: '1rem',
                                                    fontWeight: 500,
                                                    borderRadius: 2,
                                                    '&:hover': {
                                                        backgroundColor: '#0d47a1',
                                                    },
                                                }}
                                            >
                                                {loading ? 'Loggar in...' : 'Logga in'}
                                            </Button>
                                            <Box sx={{ marginTop: '12px', textAlign: 'center' }}>
                                                <Typography
                                                    variant="body2"
                                                    component="span"
                                                    onClick={() => setShowForgotPassword(true)}
                                                    sx={{
                                                        color: '#1565C0',
                                                        textDecoration: 'none',
                                                        fontSize: '0.85rem',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            textDecoration: 'underline',
                                                        },
                                                    }}
                                                >
                                                    Glömt lösenordet?
                                                </Typography>
                                            </Box>
                                        </form>
                                    )}

                                    {/* Signup form */}
                                    {tabValue === 1 && !showForgotPassword && (
                                        <>
                                            {signupSuccess ? (
                                                <Box sx={{ textAlign: 'center', py: 2 }}>
                                                    <Alert severity="success" sx={{ marginBottom: '16px' }}>
                                                        Kontot har skapats! Vi har skickat ett verifieringsmail till din e-postadress. Klicka på länken i mejlet för att aktivera ditt konto.
                                                    </Alert>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#666',
                                                            fontSize: '0.85rem',
                                                            marginBottom: '16px',
                                                        }}
                                                    >
                                                        Hittar du inte mejlet? Kolla i skräpposten eller försök logga in för att begära en ny länk.
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        onClick={() => {
                                                            setSignupSuccess(false);
                                                            setTabValue(0);
                                                        }}
                                                        sx={{
                                                            backgroundColor: '#1565C0',
                                                            color: '#fff',
                                                            textTransform: 'none',
                                                            borderRadius: 2,
                                                            '&:hover': {
                                                                backgroundColor: '#0d47a1',
                                                            },
                                                        }}
                                                    >
                                                        Gå till inloggning
                                                    </Button>
                                                </Box>
                                            ) : (
                                                <form onSubmit={handleSignup}>
                                                    <Box sx={{ marginBottom: '16px' }}>
                                                        <TextField
                                                            label="Användarnamn"
                                                            type="text"
                                                            value={signupUsername}
                                                            onChange={(e) => {
                                                                setSignupUsername(e.target.value);
                                                                setSignupError('');
                                                            }}
                                                            fullWidth
                                                            required
                                                            size="small"
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 2,
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box sx={{ marginBottom: '16px' }}>
                                                        <TextField
                                                            label="E-post"
                                                            type="email"
                                                            value={signupEmail}
                                                            onChange={(e) => {
                                                                setSignupEmail(e.target.value);
                                                                setSignupError('');
                                                            }}
                                                            fullWidth
                                                            required
                                                            size="small"
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 2,
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box sx={{ marginBottom: '16px' }}>
                                                        <TextField
                                                            label="Lösenord"
                                                            type={showSignupPassword ? 'text' : 'password'}
                                                            value={signupPassword}
                                                            onChange={handleSignupPasswordChange}
                                                            fullWidth
                                                            required
                                                            size="small"
                                                            error={!!passwordError}
                                                            helperText={
                                                                passwordError 
                                                                    ? passwordError 
                                                                    : passwordStrength === 'strong' 
                                                                        ? 'Starkt lösenord' 
                                                                        : passwordStrength === 'medium' 
                                                                            ? 'Medelstarkt lösenord' 
                                                                            : passwordStrength === 'good' 
                                                                                ? 'Bra lösenord' 
                                                                                : 'Minst 8 tecken, stor och liten bokstav, siffra och specialtecken'
                                                            }
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton
                                                                            aria-label="toggle password visibility"
                                                                            onClick={() => setShowSignupPassword(!showSignupPassword)}
                                                                            edge="end"
                                                                            size="small"
                                                                        >
                                                                            {showSignupPassword ? <VisibilityOff /> : <Visibility />}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 2,
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box sx={{ marginBottom: '16px' }}>
                                                        <TextField
                                                            label="Bekräfta lösenord"
                                                            type={showSignupConfirmPassword ? 'text' : 'password'}
                                                            value={signupConfirmPassword}
                                                            onChange={handleSignupConfirmPasswordChange}
                                                            fullWidth
                                                            required
                                                            size="small"
                                                            error={!!signupError && signupError.includes('matchar inte')}
                                                            helperText={signupError && signupError.includes('matchar inte') ? signupError : ''}
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <InputAdornment position="end">
                                                                        <IconButton
                                                                            aria-label="toggle password visibility"
                                                                            onClick={() => setShowSignupConfirmPassword(!showSignupConfirmPassword)}
                                                                            edge="end"
                                                                            size="small"
                                                                        >
                                                                            {showSignupConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                                        </IconButton>
                                                                    </InputAdornment>
                                                                ),
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 2,
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                    {signupError && !signupError.includes('matchar inte') && (
                                                        <Alert severity="error" sx={{ marginBottom: '16px', fontSize: '0.85rem' }}>
                                                            {signupError}
                                                        </Alert>
                                                    )}
                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        fullWidth
                                                        disabled={signupLoading}
                                                        sx={{
                                                            backgroundColor: '#1565C0',
                                                            color: '#fff',
                                                            textTransform: 'none',
                                                            padding: '10px',
                                                            fontSize: '1rem',
                                                            fontWeight: 500,
                                                            borderRadius: 2,
                                                            '&:hover': {
                                                                backgroundColor: '#0d47a1',
                                                            },
                                                        }}
                                                    >
                                                        {signupLoading ? 'Skapar konto...' : 'Skapa konto'}
                                                    </Button>
                                                </form>
                                            )}
                                        </>
                                    )}

                                    {/* Forgot password form */}
                                    {showForgotPassword && (
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
                                                <IconButton
                                                    onClick={() => {
                                                        setShowForgotPassword(false);
                                                        setForgotPasswordSuccess(false);
                                                        setForgotPasswordError('');
                                                        setForgotPasswordEmail('');
                                                    }}
                                                    sx={{ marginRight: '8px', padding: '4px' }}
                                                >
                                                    <ArrowBackIcon />
                                                </IconButton>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: '#1A237E',
                                                    }}
                                                >
                                                    E-post
                                                </Typography>
                                            </Box>

                                            {forgotPasswordSuccess ? (
                                                <Box sx={{ textAlign: 'center', py: 2 }}>
                                                    <Alert severity="success" sx={{ marginBottom: '16px' }}>
                                                        Om e-postadressen finns registrerad har vi skickat en återställningslänk.
                                                    </Alert>
                                                    <Button
                                                        variant="text"
                                                        onClick={() => {
                                                            setShowForgotPassword(false);
                                                            setForgotPasswordSuccess(false);
                                                        }}
                                                        sx={{
                                                            color: '#1565C0',
                                                            textTransform: 'none',
                                                        }}
                                                    >
                                                        Tillbaka till inloggning
                                                    </Button>
                                                </Box>
                                            ) : (
                                                <form onSubmit={handleForgotPassword}>
                                                    <Box sx={{ marginBottom: '8px' }}>
                                                        <TextField
                                                            label="E-post"
                                                            type="email"
                                                            placeholder="din@email.se"
                                                            value={forgotPasswordEmail}
                                                            onChange={(e) => {
                                                                setForgotPasswordEmail(e.target.value);
                                                                setForgotPasswordError('');
                                                            }}
                                                            fullWidth
                                                            required
                                                            size="small"
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    borderRadius: 2,
                                                                },
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#666',
                                                            fontSize: '0.8rem',
                                                            marginBottom: '16px',
                                                            lineHeight: 1.5,
                                                        }}
                                                    >
                                                        Ange din e-postadress så skickar vi en länk för att återställa ditt lösenord. Om du inte ser mejlet, kolla i skräppostmappen.
                                                    </Typography>
                                                    {forgotPasswordError && (
                                                        <Alert severity="error" sx={{ marginBottom: '16px', fontSize: '0.85rem' }}>
                                                            {forgotPasswordError}
                                                        </Alert>
                                                    )}
                                                    <Button
                                                        type="submit"
                                                        variant="contained"
                                                        fullWidth
                                                        disabled={forgotPasswordLoading}
                                                        sx={{
                                                            backgroundColor: '#1565C0',
                                                            color: '#fff',
                                                            textTransform: 'none',
                                                            padding: '10px',
                                                            fontSize: '1rem',
                                                            fontWeight: 500,
                                                            borderRadius: 2,
                                                            '&:hover': {
                                                                backgroundColor: '#0d47a1',
                                                            },
                                                        }}
                                                    >
                                                        {forgotPasswordLoading ? 'Skickar...' : 'Skicka återställningslänk'}
                                                    </Button>
                                                </form>
                                            )}
                                        </Box>
                                    )}
                                    </Card>
                                </Box>
                            </Grid>
                        </Grid>
                        </Container>
                    </Box>

                    {/* USP Section - 6 feature cards */}
                    <Box sx={{position: 'relative', zIndex: 2, py: { xs: 6, md: 8 } }}>
                        <Container maxWidth={false} sx={{ maxWidth: '1200px', margin: '0 auto' }}>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: { xs: '1.5rem', md: '2rem' },
                                    fontWeight: 600,
                                    color: '#1A237E',
                                    marginBottom: { xs: '32px', md: '48px' },
                                    textAlign: 'center',
                                }}
                            >
                                Allt du behöver för att dokumentera dina båtäventyr
                            </Typography>

                            <Grid container spacing={3}>
                                {uspFeatures.map((feature, index) => {
                                    const IconComponent = feature.icon;
                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={index}>
                                            <Card
                                                sx={{
                                                    height: '100%',
                                                    borderRadius: 3,
                                                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
                                                    border: '1px solid #e0e0e0',
                                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
                                                    },
                                                }}
                                            >
                                                <CardContent sx={{ padding: { xs: '24px', md: '28px' } }}>
                                                    <Box
                                                        sx={{
                                                            width: '56px',
                                                            height: '56px',
                                                            borderRadius: 2,
                                                            backgroundColor: '#E3F2FD',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            marginBottom: '16px',
                                                        }}
                                                    >
                                                        <IconComponent sx={{ fontSize: 32, color: '#1565C0' }} />
                                                    </Box>
                                                    <Typography
                                                        variant="h5"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: '#1A237E',
                                                            marginBottom: '8px',
                                                            fontSize: '1.1rem',
                                                        }}
                                                    >
                                                        {feature.title}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#424242',
                                                            lineHeight: 1.6,
                                                            fontSize: '0.9rem',
                                                        }}
                                                    >
                                                        {feature.description}
                                                    </Typography>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Container>
                    </Box>

                    {/* Getting Started Section */}
                    <Box sx={{ position: 'relative', zIndex: 2, py: { xs: 6, md: 8 } }}>
                        <Container maxWidth={false} sx={{ maxWidth: '1200px', margin: '0 auto' }}>
                            <Typography
                                variant="h2"
                                sx={{
                                    fontSize: { xs: '1.5rem', md: '2rem' },
                                    fontWeight: 600,
                                    color: '#1A237E',
                                    marginBottom: { xs: '32px', md: '48px' },
                                    textAlign: 'center',
                                }}
                            >
                                Så här kommer du igång
                            </Typography>

                            <Grid container spacing={4} justifyContent="center">
                                {gettingStartedSteps.map((step) => (
                                    <Grid item xs={12} sm={6} md={4} key={step.number}>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Box
                                                sx={{
                                                    width: '64px',
                                                    height: '64px',
                                                    borderRadius: '50%',
                                                    backgroundColor: '#1565C0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    margin: '0 auto 20px',
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        color: '#fff',
                                                        fontWeight: 700,
                                                        fontSize: '1.5rem',
                                                    }}
                                                >
                                                    {step.number}
                                                </Typography>
                                            </Box>
                                            <Typography
                                                variant="h5"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#1A237E',
                                                    marginBottom: '12px',
                                                    fontSize: '1.1rem',
                                                }}
                                            >
                                                {step.title}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#1565C0',
                                                    lineHeight: 1.6,
                                                    fontSize: '0.9rem',
                                                    maxWidth: '300px',
                                                    margin: '0 auto',
                                                }}
                                            >
                                                {step.description}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Container>
                    </Box>
                </Box>
        );
    }

    const showCreateLogbookDialog = (!hasLogbooks && !userDismissedCreateLogbookDialog) || createLogbookDialogOpen;

    // Logged in view - with welcome section and feature cards
    return (
        <Box
                sx={{
                    minHeight: '100vh',
                    backgroundColor: '#F5F9FF',
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(to bottom, rgba(245, 249, 255, 0.95) 0%, rgba(245, 249, 255, 0.98) 100%)',
                        zIndex: 0,
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundImage: 'url(/images/bakgrund_loggboken_1000x1500.jpg)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        opacity: 0.20,
                        zIndex: 0,
                    },
                }}
            >
                {/* Welcome Section */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: { xs: '60px 20px', md: '100px 40px' },
                        textAlign: 'center',
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    <Box sx={{ maxWidth: '800px' }}>
                        <Typography
                            variant="h1"
                            sx={{
                                fontSize: { xs: '2rem', md: '3rem' },
                                fontWeight: 700,
                                color: '#1A237E',
                                marginBottom: '20px',
                            }}
                        >
                            Välkommen
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', md: '1.25rem' },
                                color: '#212121',
                                lineHeight: 1.6,
                                maxWidth: '600px',
                                margin: '0 auto',
                            }}
                        >
                            Dokumentera dina båtäventyr och båtfix med text och bilder. Allt på ett säkert ställe, bara för dig och dem du bjuder in.
                        </Typography>
                    </Box>
                </Box>

                {/* Feature Cards for Logged In Users - hide when no logbooks (dialog is shown instead) */}
                {hasLogbooks && (
                <Container maxWidth={false} sx={{ padding: { xs: '40px 20px', md: '60px 20px' }, position: 'relative', zIndex: 1, maxWidth: '1200px', margin: '0 auto' }}>
                    <Grid container spacing={3} sx={{ marginBottom: 5 }}>
                        <Grid item xs={12} sm={6}>
                            <Card
                                sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                                    },
                                }}
                            >
                                <CardContent sx={{ padding: '40px', textAlign: 'center' }}>
                                    <Box
                                        sx={{
                                            width: '80px',
                                            height: '80px',
                                            borderRadius: 2,
                                            backgroundColor: '#E3F2FD',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto 24px',
                                        }}
                                    >
                                        <DirectionsBoatIcon sx={{ fontSize: 48, color: '#1565C0' }} />
                                    </Box>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#1A237E',
                                            marginBottom: '16px',
                                        }}
                                    >
                                        Tripploggar
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#212121',
                                            marginBottom: '24px',
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        Skapa och dela dina båtäventyr med loggar, bilder och beskrivningar
                                    </Typography>
                                    <Button
                                        component={Link}
                                        to="/home"
                                        variant="contained"
                                        sx={{
                                            backgroundColor: '#E3F2FD',
                                            color: '#1565C0',
                                            textTransform: 'none',
                                            padding: '10px 24px',
                                            fontWeight: 500,
                                            '&:hover': {
                                                backgroundColor: '#BBDEFB',
                                            },
                                        }}
                                    >
                                        Visa tripploggar
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Card
                                sx={{
                                    height: '100%',
                                    borderRadius: 3,
                                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.15)',
                                    },
                                }}
                            >
                                <CardContent sx={{ padding: '40px', textAlign: 'center' }}>
                                    <Box
                                        sx={{
                                            width: '80px',
                                            height: '80px',
                                            borderRadius: 2,
                                            backgroundColor: '#E3F2FD',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto 24px',
                                        }}
                                    >
                                        <BuildIcon sx={{ fontSize: 48, color: '#1565C0' }} />
                                    </Box>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#1A237E',
                                            marginBottom: '16px',
                                        }}
                                    >
                                        Serviceloggar
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#212121',
                                            marginBottom: '24px',
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        Spara dina serviceloggar med beskrivningar och bilder
                                    </Typography>
                                    <Button
                                        component={Link}
                                        to="/servicelogs"
                                        variant="contained"
                                        sx={{
                                            backgroundColor: '#E3F2FD',
                                            color: '#1565C0',
                                            textTransform: 'none',
                                            padding: '10px 24px',
                                            fontWeight: 500,
                                            '&:hover': {
                                                backgroundColor: '#BBDEFB',
                                            },
                                        }}
                                    >
                                        Visa serviceloggar
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
                )}

                {/* Buttons: Skapa en ny loggbok, Gå med i en loggbok - always visible when logged in */}
                <Container maxWidth={false} sx={{ padding: { xs: '20px 20px', md: '20px 20px' }, position: 'relative', zIndex: 1, maxWidth: '1200px', margin: '0 auto' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', marginBottom: 2 }}>
                        <Button variant="contained" color="primary" onClick={handleOpenCreateLogbookClick}>
                            Skapa en ny loggbok
                        </Button>
                        <Button component={Link} to="/join-logbook" variant="outlined" color="primary">
                            Gå med i en loggbok
                        </Button>
                    </Box>
                </Container>

                <Dialog
                    open={confirmSecondLogbookOpen}
                    onClose={() => setConfirmSecondLogbookOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Skapa ytterligare loggbok?</DialogTitle>
                    <DialogContent>
                        <Typography color="text.secondary" sx={{ mb: 1 }}>
                            Är du säker på att du behöver ytterligare en loggbok?
                        </Typography>
                        <Typography color="text.secondary">
                            {`Du har redan skapat en loggbok med namnet "${firstOwnedLogbookDisplayName}".`}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmSecondLogbookOpen(false)}>Avbryt</Button>
                        <Button variant="contained" onClick={handleConfirmSecondLogbookContinue}>
                            Fortsätt
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Create logbook dialog - shown when no logbooks or when user clicks "Skapa en ny loggbok" */}
                <Dialog
                    open={showCreateLogbookDialog}
                    onClose={hasLogbooks ? () => setCreateLogbookDialogOpen(false) : () => setUserDismissedCreateLogbookDialog(true)}
                    disableEscapeKeyDown={!hasLogbooks}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>{hasLogbooks ? 'Skapa en ny loggbok' : 'Skapa din första loggbok'}</DialogTitle>
                    <DialogContent>
                        <Typography color="text.secondary" sx={{ mb: 2 }}>
                            Ge loggboken ett namn, t.ex. båtens namn eller familjens namn (valfritt).
                        </Typography>
                        {createLogbookError && (
                            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCreateLogbookError('')}>
                                {createLogbookError}
                            </Alert>
                        )}
                        <TextField
                            fullWidth
                            label="Namn på loggboken"
                            value={newLogbookName}
                            onChange={(e) => setNewLogbookName(e.target.value)}
                            placeholder="t.ex. båtens namn eller familjens namn"
                            disabled={creatingLogbook}
                        />
                        <Box sx={{ mt: 2 }}>
                            <Button component={Link} to="/join-logbook" variant="text" color="primary">
                                Gå med i en loggbok i stället
                            </Button>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => { setCreateLogbookDialogOpen(false); if (!hasLogbooks) setUserDismissedCreateLogbookDialog(true); }}>
                            Avbryt
                        </Button>
                        <Button variant="contained" onClick={handleCreateLogbook} disabled={creatingLogbook}>
                            Skapa
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Main Content - Only show when logged in */}
                <Container maxWidth={false} sx={{ padding: { xs: '40px 20px', md: '60px 20px' }, position: 'relative', zIndex: 1, maxWidth: '1200px', margin: '0 auto' }}>


                    <Box sx={{ marginTop: '20px' }}>






                    </Box>
                </Container>
            </Box>
    );
}

export default Info;
